import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { m } from 'motion/react';
import { X } from 'lucide-react';
import { isTopDialog, useDialog } from '../hooks/useDialog';
import { useT } from '../hooks/useT';

interface ModalProps {
  title: string;
  onClose: () => void;
  /** Enter bevestigt (bv. "Overnemen"); laat weg voor dialogen zonder primaire actie */
  onConfirm?: () => void;
  children: ReactNode;
}

/** Basis-dialoog: portal naar body, focus-trap, Escape sluit, klik op de achtergrond sluit. */
export function Modal({ title, onClose, onConfirm, children }: ModalProps) {
  const { t } = useT();
  const titleId = useId();
  const dialogRef = useDialog(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isTopDialog(dialogRef.current)) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && onConfirm && !(e.target instanceof HTMLButtonElement)) onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onConfirm, dialogRef]);

  return createPortal(
    <m.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-[120] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <m.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-md rounded-3xl bg-slate-50 p-6 text-slate-900 shadow-xl dark:border dark:border-white/10 dark:bg-slate-900 dark:text-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 id={titleId} className="text-lg font-bold">{title}</h2>
          <button
            onClick={onClose}
            aria-label={t('closeBtn')}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-black/5 transition-all duration-150 active:scale-95 dark:bg-slate-800/80"
          >
            <X size={20} aria-hidden />
          </button>
        </div>
        {children}
      </m.div>
    </m.div>,
    document.body,
  );
}
